import { useEffect, useState } from "react";
import { verifyUsdRoutingNumber } from "api/fetchers";

export default function useUsdRoutingVerification({
    businessId,
    currency,
    currencyTo,
    source,
    enabled,
    routingNumber,
}) {
    const [details, setDetails] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const normalizedRoutingNumber = String(routingNumber || "").replace(/\D/g, "");

        if (!enabled || !businessId || normalizedRoutingNumber.length !== 9) {
            setDetails(null);
            setError("");
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setDetails(null);
            setError("");
            setLoading(true);

            try {
                const verifiedDetails = await verifyUsdRoutingNumber({
                    businessId,
                    routingNumber: normalizedRoutingNumber,
                    currency,
                    currencyTo,
                    source,
                    signal: controller.signal,
                });

                if (!verifiedDetails.bankName) {
                    throw new Error("The routing number could not be verified.");
                }

                setDetails(verifiedDetails);
            } catch (requestError) {
                if (requestError?.name === "CanceledError" || requestError?.name === "AbortError") {
                    return;
                }
                setError(requestError?.response?.data?.message || requestError?.message || "Unable to verify routing number.");
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, 500);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [businessId, currency, currencyTo, enabled, routingNumber, source]);

    return { details, error, loading };
}
